import React, { FC, ReactNode, useCallback, useContext, useEffect } from 'react'
import { CreditCard } from '@afterpaytouch/portal-api'

import { ReportCardCancellationModalContext, ReportCardCancellationModalProvider, ReportCardCancellationSteps } from './ReportCardCancellationModalContext'
import { Modal } from '@afterpaytouch/core'
import { useReportCardCancellation } from './hooks'

import { ReportCardCancellationNavigation } from './ReportCardCancellationNavigation'
import { Decline } from './Decline'
import { SelectCard } from './SelectCard'
import { Confirm } from './Confirm'

interface ReportCardCancellationModalProps {
  isModalOpen: boolean
  closeModal: () => void
  defaultPaymentMethod: CreditCard
  refetchCard: () => void
}

export const ReportCardCancellationModalSteps: FC<Omit<ReportCardCancellationModalProps, 'defaultPaymentMethod' | 'refetchCard'>> = ({
  isModalOpen,
  closeModal,
}) => {
  const { step, isLoading, resetState } = useContext(ReportCardCancellationModalContext)
  const { init } = useReportCardCancellation()

  const renderStep: () => ReactNode = useCallback(() => {
    switch (step) {
      case ReportCardCancellationSteps.Decline:
        return <Decline />
      case ReportCardCancellationSteps.SelectCard:
        return <SelectCard />
      case ReportCardCancellationSteps.Confirm:
        return <Confirm />
    }
    return null
  }, [step])

  useEffect(() => {
    init()
    return () => {
      resetState()
    }
  }, [])

  return (
    <Modal
      show={isModalOpen}
      onClose={closeModal}
      onBackdropClick={closeModal}
      onHide={closeModal}
      testNameSpace='report-card-cancellation'
      loading={isLoading}
    >
      <Modal.Header divider={true}>
        <ReportCardCancellationNavigation />
      </Modal.Header>
      <Modal.Content>{renderStep()}</Modal.Content>
    </Modal>
  )
}

export const ReportCardCancellationModal: FC<ReportCardCancellationModalProps> = ({ isModalOpen, closeModal, defaultPaymentMethod, refetchCard }) => {
  return isModalOpen ? (
    <ReportCardCancellationModalProvider closeModal={closeModal} defaultPaymentMethod={defaultPaymentMethod} refetchCard={refetchCard}>
      <ReportCardCancellationModalSteps isModalOpen={isModalOpen} closeModal={closeModal} />
    </ReportCardCancellationModalProvider>
  ) : null
}
